/**
 * The page saying it could not start.
 *
 * `main.ts` ran `void boot()`, which discards the promise. Every `throw` on the
 * boot path — the editor door refusing, the document vocabulary refusing, a
 * bootstrap that can neither be written nor rolled back, a browser that will
 * not mint an id — landed as an unhandled rejection: the shell's static chrome
 * stayed on screen, `#pools` and `#stage` stayed empty, and the only trace was
 * a console line nobody reading the page can see.
 *
 * That is the worst shape a failure can take here, because it is the shape of
 * SUCCESS for somebody with nothing stored. A returning person whose graph this
 * bundle could not open saw a page indistinguishable from an empty account, and
 * had no reason to think their objects were still there. They are.
 *
 * Its own module rather than a closure in `main.ts` because a boundary nobody
 * can call is a boundary nobody can test, and this one has to be right in
 * exactly the circumstances where everything else is wrong.
 */

/** What the panel says, kept here so a test names the same string the page does. */
export const BOOT_FAILURE_HEADING = 'Morph could not start';
export const BOOT_FAILURE_NOTE = 'Nothing has been changed or removed. What is '
  + 'stored is still stored — this page could not open it.';

/** The stack where there is one, because the difference between "the editor
 *  door refused" and a `VersionError` from IndexedDB is the whole of what a
 *  report needs to carry. A bare message loses the frame that names the door. */
export function describeBootFailure(error: unknown): string {
  if (error instanceof Error) return error.stack ?? `${error.name}: ${error.message}`;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

/**
 * Render the failure into `host`, replacing whatever was there.
 *
 * Deliberately does NOT touch the store, the bootstrap, or the database.
 * Clearing them would produce a page that renders, by destroying exactly the
 * objects the person came back for. Recovery, if there is one, is a decision
 * somebody makes with the error in front of them.
 *
 * `role="alert"` so it reaches a screen reader without moving focus — the
 * person may already be reading something else on the page.
 */
export function renderBootFailure(host: Element, error: unknown): void {
  const doc = host.ownerDocument;
  const panel = doc.createElement('section');
  panel.className = 'boot-failure';
  panel.setAttribute('role', 'alert');
  panel.setAttribute('data-morph-boot-failure', 'true');

  const heading = doc.createElement('h2');
  heading.textContent = BOOT_FAILURE_HEADING;

  const note = doc.createElement('p');
  note.textContent = BOOT_FAILURE_NOTE;

  // `pre` with `textContent`, never `innerHTML`: the message can carry anything
  // a thrown value carried, including text out of a person's own document.
  const detail = doc.createElement('pre');
  detail.className = 'boot-failure-detail';
  detail.textContent = describeBootFailure(error);

  panel.append(heading, note, detail);
  host.replaceChildren(panel);
}
